import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { useXHR } from "@sembark-travel/xhr"
import React, { useMemo } from "react"
import { TouristProfileForm, TTouristProfile } from "../TouristProfiles"
import { parseDate } from "@sembark-travel/datetime-utils"
import { transformAddressToEditableValue } from "../Addresses"
import { addLabelToAirport } from "../Flights"

function EditTouristProfile({
	touristProfile,
	onSuccess,
	onCancel,
	...props
}: Omit<
	React.ComponentProps<typeof TouristProfileForm>,
	"onSubmit" | "initialValues"
> & {
	touristProfile: TTouristProfile
	onSuccess: (touristProfile: TTouristProfile) => void
	onCancel?: () => void
}) {
	const touristProfileId = touristProfile.id
	const xhr = useXHR()

	const initialValues = useMemo(() => {
		const {
			id,
			type,
			salutation,
			name_without_salutation,
			dob,
			nationality,
			phone_numbers,
			email,
			address,

			meal_preference,
			stay_preference,
			language_preference,
			preferred_airport,
			preferred_flight_class,
			marriage_anniversary,
			is_smoker,
			tags,
			passport_number,
			passport_expiry_date,
			company_name,
			gst_number,
			remarks,
		} = touristProfile

		return {
			id: id ?? null,
			type: type ?? "adult",
			salutation: salutation ?? undefined,
			name: name_without_salutation ?? "",
			dob: dob ? parseDate(dob) : undefined,
			nationality: nationality ?? undefined,
			phone_numbers: (phone_numbers ?? []).map((p) => ({
				country_code: p.country_code ?? "",
				phone_number: p.phone_number ?? "",
				number: p.number ?? "",
				is_primary: p.is_primary,
				has_whatsapp: p.has_whatsapp,
			})),
			email: email ?? undefined,
			address: address ? transformAddressToEditableValue(address) : undefined,
			meal_preference: meal_preference
				? { id: meal_preference, name: meal_preference }
				: undefined,
			stay_preference: stay_preference
				? { id: stay_preference, name: stay_preference }
				: undefined,
			language_preference: language_preference
				? { id: language_preference, name: language_preference }
				: undefined,
			preferred_airport: preferred_airport
				? addLabelToAirport(preferred_airport)
				: undefined,
			preferred_flight_class: preferred_flight_class ?? "",
			marriage_anniversary: marriage_anniversary ?? "",
			is_smoker:
				is_smoker === null
					? null
					: is_smoker
						? ("Yes" as const)
						: ("No" as const),
			tags: tags ?? [],
			passport_number: passport_number ?? "",
			passport_expiry_date: passport_expiry_date
				? parseDate(passport_expiry_date)
				: undefined,
			company_name: company_name ?? "",
			gst_number: gst_number ?? "",
			remarks: remarks ?? "",
		}
	}, [touristProfile])

	return (
		<TouristProfileForm
			initialValues={initialValues}
			onSubmit={async (values) => {
				const touristProfile = await xhr.patch(
					`/tourist-profiles/${touristProfileId}`,
					values
				)
				showSnackbar("Tourist profile details updated successfully.")
				onSuccess(touristProfile.data.data)
				return touristProfile
			}}
			onCancel={onCancel}
			{...props}
		/>
	)
}

export function EditTouristProfileDialog({
	touristProfile,
	hideBasicInfo,
	onSuccess,
	children,
}: {
	touristProfile: TTouristProfile
	hideBasicInfo?: boolean
	onSuccess: (touristProfile: TTouristProfile) => void
	children: (props: { show: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ show: open })}
			<Dialog
				open={isOpen}
				onClose={close}
				title={`Edit ${touristProfile.name_without_salutation} Details`}
				lg
			>
				<Dialog.Body>
					<EditTouristProfile
						touristProfile={touristProfile}
						hideBasicInfo={hideBasicInfo}
						onSuccess={(touristProfile) => {
							onSuccess(touristProfile)
							close()
						}}
						onCancel={() => close()}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}
